import { contains, geodesicLength } from '@arcgis/core/geometry/geometryEngine';
import Point from '@arcgis/core/geometry/Point';
import Polyline from '@arcgis/core/geometry/Polyline';
import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils';
import { DEFAULT_MAP_SOURCE } from '@env';
import { Position } from 'geojson';
import { Observable, Subject } from 'rxjs';

// Removed EsriMap import to avoid circular dependency
import { FsMap } from '@lib/geo/map';

import { SnLonLat } from '../../coordinates';

export class FsMapShape {
  public map: FsMap;

  public get defaultMapSource(): string {
    return DEFAULT_MAP_SOURCE;
  }

  private readonly _newClick$: Subject<any> = new Subject<any>();
  private readonly _newHover$: Subject<any> = new Subject<any>();
  private readonly _mouseOut$: Subject<any> = new Subject<any>();

  get newClick$(): Observable<any> {
    return this._newClick$.asObservable();
  }

  get newHover$(): Observable<any> {
    return this._newHover$.asObservable();
  }

  get mouseOut$(): Observable<any> {
    return this._mouseOut$.asObservable();
  }

  constructor(public shape?: any) {}

  public getPath(): Position[] {
    let path;
    const shape = this.shape?.shape || this.shape;
    switch (this.defaultMapSource) {
      case 'google':
        if (shape instanceof google.maps.Circle) {
          path = this._googleCirclePath(shape.getCenter(), shape.getRadius());
          path.push(path[0]);
        } else {
          path = shape
            .getPath()
            .getArray()
            .map((latLng) => [latLng.lng(), latLng.lat()]);
        }
        return path;
      case 'esri':
        if (this.shape.geometry?.paths) {
          path = this.shape.geometry.spatialReference.isWebMercator
            ? this.shape.geometry.paths[0].map((position) =>
                webMercatorUtils.xyToLngLat(position[0], position[1]),
              )
            : this.shape.geometry.paths[0];
        } else {
          path = this.shape.geometry.spatialReference.isWebMercator
            ? this.shape.geometry.rings[0].map((position) =>
                webMercatorUtils.xyToLngLat(position[0], position[1]),
              )
            : this.shape.geometry.rings[0];
        }

        return path;
    }
  }

  public getBounds(): {
    north: number;
    south: number;
    east: number;
    west: number;
  } {
    let bounds;
    const shape = this.shape?.shape || this.shape;
    switch (this.defaultMapSource) {
      case 'google':
        if (shape.getBounds) {
          bounds = shape.getBounds();
        } else {
          bounds = new google.maps.LatLngBounds();
          const paths = shape.getPath();
          paths.forEach((path) => {
            bounds.extend(path);
          });
        }

        return {
          north: bounds.getNorthEast().lat(),
          south: bounds.getSouthWest().lat(),
          east: bounds.getNorthEast().lng(),
          west: bounds.getSouthWest().lng(),
        };
      case 'esri':
        bounds = shape.geometry.spatialReference.isWebMercator
          ? webMercatorUtils.webMercatorToGeographic(shape.geometry.extent)
          : shape.geometry.extent;
        return {
          north: bounds.ymax,
          south: bounds.ymin,
          east: bounds.xmax,
          west: bounds.xmin,
        };
    }
  }

  public getCenter(): {
    center: number[];
  } {
    const shape = this.shape?.shape || this.shape;
    let center;
    switch (this.defaultMapSource) {
      case 'google':
        shape instanceof google.maps.Circle
          ? (center = shape.getCenter())
          : (center = this._genGoogleShapeCenter());

        return {
          center: [center?.lat(), center?.lng()],
        };
      case 'esri':
        shape.geometry?.centroid
          ? (center = shape.geometry.centroid)
          : (center = shape.geometry.extent?.center);

        return {
          center: [center.latitude, center.longitude],
        };
    }
  }

  public getCenterPoint(): Point {
    const ring = this.shape.geometry?.rings[0] || this.shape.shape?.geometry?.rings[0];

    let xSum = 0;
    let ySum = 0;
    const totalPoints = ring.length;

    ring.forEach((point: number[]) => {
      xSum += point[0];
      ySum += point[1];
    });

    const centroidX = xSum / totalPoints;
    const centroidY = ySum / totalPoints;

    return new Point({
      x: centroidX,
      y: centroidY,
      spatialReference:
        this.shape.geometry?.spatialReference || this.shape.shape?.geometry?.spatialReference,
    });
  }

  public getNorthEast(): {
    northEast: number[];
  } {
    const bounds = this.getBounds();
    return {
      northEast: [bounds.north, bounds.east],
    };
  }

  public getSouthWest(): {
    southWest: number[];
  } {
    const bounds = this.getBounds();
    return {
      southWest: [bounds.south, bounds.west],
    };
  }

  public getRadius(): {
    radius: number;
  } {
    const shape = this.shape?.shape || this.shape;
    let radius;
    switch (this.defaultMapSource) {
      case 'google':
        radius = shape.getRadius();
        return {
          radius,
        };
      case 'esri':
        const center = this.getCenterPoint();

        const firstVertex = shape.geometry.rings[0][0];
        let firstVertexPoint = new Point({
          x: firstVertex[0],
          y: firstVertex[1],
          spatialReference: shape.geometry.spatialReference,
        });
        if (shape.geometry.spatialReference.isWebMercator) {
          firstVertexPoint = webMercatorUtils.webMercatorToGeographic(firstVertexPoint) as Point;
        }

        let centerPoint = center;
        if (center.spatialReference.isWebMercator) {
          centerPoint = webMercatorUtils.webMercatorToGeographic(center) as Point;
        }

        const line = new Polyline({
          paths: [
            [
              [centerPoint.x, centerPoint.y],
              [firstVertexPoint.x, firstVertexPoint.y],
            ],
          ],
          spatialReference: { wkid: 4326 },
        });

        return { radius: geodesicLength(line, 'meters') };
    }
  }

  public contains(point: { lat: number; lng: number }): any {
    switch (this.defaultMapSource) {
      case 'google':
        return this.shape.getBounds().contains(point);
      case 'esri':
        const xyPoint = new Point({
          latitude: point.lat,
          longitude: point.lng,
        });
        return this.shape.geometry.contains(xyPoint);
    }
  }

  public setMap(map: FsMap): void {
    map?.addShape(this.shape);
    this.map = map;
  }

  public removeMap(shape: FsMapShape): void {
    this.map.removeShape(shape.shape);
  }

  public removeShape(map: FsMap): void {
    map.removeShape(this.shape);
    this.map = map;
  }

  public pointInPolygon(point: SnLonLat) {
    switch (this.defaultMapSource) {
      case 'google':
        const googlePoint = new google.maps.LatLng(point.lat, point.lon);
        return google.maps.geometry.poly.containsLocation(googlePoint, this.shape);
      case 'esri':
        let esriPoint = new Point({
          y: point.lat,
          x: point.lon,
          spatialReference: { wkid: 4326 },
        });

        if (this.shape.geometry.spatialReference.isWebMercator) {
          esriPoint = webMercatorUtils.geographicToWebMercator(esriPoint) as Point;
        }

        return contains(this.shape.geometry, esriPoint);
    }
  }

  public pointInCircle(point: SnLonLat) {
    const { radius } = this.getRadius();
    const shape = this.shape?.shape || this.shape;

    switch (this.defaultMapSource) {
      case 'google':
        const { center } = this.getCenter();
        const pointLatLng = new google.maps.LatLng(point.lat, point.lon);
        const centerLatLng = new google.maps.LatLng(center[0], center[1]);
        return (
          google.maps.geometry.spherical.computeDistanceBetween(pointLatLng, centerLatLng) <= radius
        );
      case 'esri':
        let centerPoint = this.getCenterPoint();
        if (centerPoint.spatialReference.isWebMercator) {
          centerPoint = webMercatorUtils.webMercatorToGeographic(centerPoint) as Point;
        }
        const pointToCheck = new Point({
          y: point.lat,
          x: point.lon,
          spatialReference: { wkid: 4326 },
        });

        const line = new Polyline({
          paths: [
            [
              [centerPoint.x, centerPoint.y],
              [pointToCheck.x, pointToCheck.y],
            ],
          ],
          spatialReference: shape.geometry.spatialReference,
        });

        const pointDistance = geodesicLength(line, 'meters');

        return pointDistance <= radius;

      default:
        return false;
    }
  }

  public setupEsriClickHandler(): void {
    const mapView = (this.map as any).mapView;
    mapView.on('click', (event) => {
      mapView.hitTest(event).then((response: any) => {
        if (response.results.length > 0) {
          if (response.results.length > 1 && response.results[1].layer?.id === 'iconsLayer') {
            return;
          }
          const graphicResult = response.results.find(
            (result) => result.graphic.attributes?.id === this.shape.attributes?.id,
          );

          if (graphicResult) {
            this._onClick(graphicResult.graphic);
          }
        }
      });
    });
  }

  public listenForGoogleShapeEvents(): void {
    this.shape.addListener('click', () => {
      this._onClick(this.shape);
    });

    this.shape.addListener('mouseover', () => {
      this._onHover(this.shape);
    });

    this.shape.addListener('mouseout', () => {
      this._mouseOut(this.shape);
    });
  }

  public setStyles(newStyles: any): void {
    switch (this.defaultMapSource) {
      case 'google':
        this.shape.setOptions(newStyles);
        break;
      case 'esri':
        const graphic = this.shape;
        graphic.symbol = {
          ...graphic.symbol,
          ...newStyles,
        };
        (this.map as any).mapView.graphics.add(graphic);
        break;
    }
  }

  private _onClick(object: any): void {
    this._newClick$.next(object);
  }

  private _onHover(object: any): void {
    this._newHover$.next(object);
  }

  private _mouseOut(object: any): void {
    this._mouseOut$.next(object);
  }

  private _genGoogleShapeCenter(): any {
    if (this.shape instanceof google.maps.Rectangle) {
      return this.shape.getBounds().getCenter();
    }
    const paths = this.shape.getPath();
    let latSum = 0;
    let lngSum = 0;
    const numPoints = paths.getLength();

    for (let i = 0; i < numPoints; i++) {
      const point = paths.getAt(i);
      latSum += point.lat();
      lngSum += point.lng();
    }

    const center = new google.maps.LatLng(latSum / numPoints, lngSum / numPoints);

    return center;
  }

  private _googleCirclePath(
    center: google.maps.LatLng,
    radius: number,
    points: number = 360,
  ): number[] {
    const paths = [];
    const factor = 360 / points;
    let distance = 0;
    for (let i = 0; i < points; i += 1, distance += factor) {
      const lat = google.maps.geometry.spherical.computeOffset(center, radius, distance).lat();
      const lng = google.maps.geometry.spherical.computeOffset(center, radius, distance).lng();
      paths.push([lat, lng]);
    }
    return paths;
  }
}
